import {
  CreateDocumentRequest,
  DocumentsRequest,
  GetDocumentRequest,
  UpdateDocumentRequest,
  DeleteDocumentRequest,
  DownloadDocumentRequest,
} from '../api/requests';
import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { RequestPagination, UploadSingleFile } from '~/common/types';
import type { Document, DocumentID } from '../types';
import { Category } from '~/categories/types';

export class DocumentService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async create(
    document: UploadSingleFile,
    onUploadProgress: (progressEvent: any) => void,
    signal?: AbortSignal,
  ) {
    const accessToken = this.userStore.accessToken;

    const formData = this.documentCreateTransformer(document);

    const request = new CreateDocumentRequest({
      token: accessToken,
      document: formData,
    });

    return this.apiClient.request(request, {
      onUploadProgress: onUploadProgress,
      signal: signal,
    });
  }

  async fetch(pagination: RequestPagination, params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new DocumentsRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    return this.apiClient.request(request);
  }

  async get(id: DocumentID, params?: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new GetDocumentRequest({
      token: accessToken,
      id,
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async download(id: DocumentID) {
    const accessToken = this.userStore.accessToken;
    const request = new DownloadDocumentRequest({
      token: accessToken,
      id,
    });

    return this.apiClient.request(request, { responseType: 'blob' });
  }

  async update(
    documentID: DocumentID,
    document: Document,
    params?: { include: string },
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateDocumentRequest({
      token: accessToken,
      documentID,
      document: this.documentUpdateTransformer(document),
      params: { ...params },
    });

    return this.apiClient.request(request);
  }

  async delete(id: DocumentID) {
    const accessToken = this.userStore.accessToken;
    const request = new DeleteDocumentRequest({
      token: accessToken,
      id,
    });

    return this.apiClient.request(request);
  }

  documentCreateTransformer(document: UploadSingleFile) {
    const formData = new FormData();
    if (document.file) {
      formData.append('file', document.file);
    }
    if (document.title !== undefined) {
      formData.append('title', document.title);
    }
    formData.append('categories', JSON.stringify(document.categories));

    return formData;
  }

  documentUpdateTransformer(document: Document) {
    document.categoriesID = document.categories?.data.map(
      (category: Category) => category.id,
    );
    return document;
  }
}
